import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import type * as models from './models'
import { State } from './state'

/**
 * Core actions for this feature.
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /* eslint-disable sort-keys */
    /**
     * Loads the agent-default and (optionally) per-conversation override
     * for the requested agent from the backend.
     */
    load: store.actions.request({
      input: store.actions.Data.asIs<models.LoadRequest>(),
      output: store.actions.Data.asIs<models.LoadResponse>(),
      reducers: {
        cancellation: ({
          state,
          data: {
            request: { agentId, conversationId },
          },
        }): State =>
          State.helper(state)
            .setAgentStatus(agentId, undefined)
            .setConversationStatus(conversationId, undefined),
        error: ({
          state,
          data: {
            request: { agentId, conversationId },
            error,
          },
        }): State =>
          State.helper(state)
            .setAgentStatus(agentId, { error })
            .setConversationStatus(conversationId, { error }),
        request: ({ state, data: { agentId, conversationId } }): State =>
          State.helper(state)
            .setAgentStatus(agentId, { isLoading: true })
            .setConversationStatus(conversationId, { isLoading: true }),
        success: ({
          state,
          data: {
            request: { agentId, conversationId },
            response: {
              agentDefault,
              conversationOverride,
              knowledgeBaseFiles,
            },
          },
        }): State =>
          State.helper(state)
            .setAgentValue(agentId, agentDefault)
            .setConversationValue(conversationId, conversationOverride)
            .setAgentAvailableKnowledgeBaseFiles(agentId, knowledgeBaseFiles),
      },
    }),
    /**
     * Deletes a per-conversation override, restoring inheritance from
     * the agent default.
     */
    deleteOverride: store.actions.request({
      input: store.actions.Data.asIs<models.DeleteOverrideRequest>(),
      output: store.actions.Data.asIs<void>(),
      reducers: {
        cancellation: ({
          state,
          data: {
            request: { conversationId },
          },
        }): State =>
          State.helper(state).setConversationStatus(conversationId, undefined),
        error: ({
          state,
          data: {
            request: { conversationId },
            error,
          },
        }): State =>
          State.helper(state).setConversationStatus(conversationId, { error }),
        request: ({ state, data: { conversationId } }): State =>
          State.helper(state).setConversationStatus(conversationId, {
            isSubmitting: true,
          }),
        success: ({
          state,
          data: {
            request: { conversationId },
          },
        }): State =>
          State.helper(state).deleteConversation(conversationId).clearEditing(),
      },
    }),
    /**
     * Persists a configuration for the agent default or conversation
     * override scope.
     */
    save: store.actions.request({
      input: store.actions.Data.asIs<models.SaveRequest>(),
      output: store.actions.Data.asIs<models.SaveResponse>(),
      reducers: {
        cancellation: ({
          state,
          data: {
            request: { key },
          },
        }): State =>
          key.agentId !== undefined
            ? State.helper(state).setAgentStatus(key.agentId, undefined)
            : State.helper(state).setConversationStatus(
                key.conversationId,
                undefined,
              ),
        error: ({
          state,
          data: {
            request: { key },
            error,
          },
        }): State =>
          key.agentId !== undefined
            ? State.helper(state).setAgentStatus(key.agentId, { error })
            : State.helper(state).setConversationStatus(key.conversationId, {
                error,
              }),
        request: ({ state, data: { key } }): State =>
          key.agentId !== undefined
            ? State.helper(state).setAgentStatus(key.agentId, {
                isSubmitting: true,
              })
            : State.helper(state).setConversationStatus(key.conversationId, {
                isSubmitting: true,
              }),
        success: ({
          state,
          data: {
            request: { key },
            response,
          },
        }): State =>
          key.agentId !== undefined
            ? State.helper(state)
                .setAgentValue(key.agentId, response)
                .clearEditing()
            : State.helper(state)
                .setConversationValue(key.conversationId, response)
                .clearEditing(),
      },
    }),
    /**
     * Opens an editing session for the given scope. The component reads
     * the resulting `editing` slice (via selectors) to decide whether to
     * render the configuration modal.
     *
     * A companion effect dispatches {@link load} so the freshest
     * persisted value is available to seed the form.
     */
    startEditing: store.actions.standalone({
      data: store.actions.Data.asIs<models.StartEditingRequest>(),
      reducer: ({ state, data: { agentId, conversationId } }): State =>
        State.helper(state).setEditing({ agentId, conversationId }),
    }),
    /**
     * Closes the editing session, discarding any in-flight drafts.
     * Dispatched on Cancel / backdrop click.
     */
    stopEditing: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }): State => State.helper(state).clearEditing(),
    }),
    /** Records the user's in-progress agent-default draft. */
    setAgentDraft: store.actions.standalone({
      data: store.actions.Data.asIs<models.SetAgentDraftRequest>(),
      reducer: ({ state, data: { context } }): State =>
        State.helper(state).setAgentDraft(context),
    }),
    /** Records the user's in-progress per-conversation draft. */
    setConversationDraft: store.actions.standalone({
      data: store.actions.Data.asIs<models.SetConversationDraftRequest>(),
      reducer: ({ state, data: { context } }): State =>
        State.helper(state).setConversationDraft(context),
    }),
    /* eslint-enable */
  } as const satisfies store.actions.Config.Constraint<State>
}
